import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowUpRight, Check, ExternalLink } from 'lucide-react'
import SectionHeading from './SectionHeading'
import { FEATURED_PROJECTS } from '../data/projects'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

const Projects: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-project-card]',
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.15,
          scrollTrigger: { trigger: ref.current, start: 'top 78%', once: true },
        }
      )
    }, ref)
    return () => ctx.revert()
  }, [])

  const openProject = (id: string) => () => navigate(`/projects/${id}`)

  return (
    <section
      id="projects"
      ref={ref}
      className="section-padding bg-neutral-50 dark:bg-neutral-900/30"
    >
      <div className="container-width">
        <SectionHeading
          label="Projects"
          meta="case studies"
          title={
            <>
              Platform work, <span className="text-gradient">in depth</span>
            </>
          }
          description="Two full engineering case studies — the problem, why it was hard, how I solved it, and what shipping it actually changed. Each opens into its own detailed walkthrough."
        />

        <div className="grid gap-6 lg:grid-cols-2">
          {FEATURED_PROJECTS.map((project, i) => (
            <div
              key={project.id}
              data-project-card
              role="link"
              tabIndex={0}
              onClick={openProject(project.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(`/projects/${project.id}`)
                }
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                e.currentTarget.style.setProperty(
                  '--spot-x',
                  `${e.clientX - rect.left}px`
                )
                e.currentTarget.style.setProperty(
                  '--spot-y',
                  `${e.clientY - rect.top}px`
                )
              }}
              className="spotlight group relative flex cursor-pointer flex-col rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-7 sm:p-8 outline-none transition-all duration-300 hover:border-primary-500/40 hover:shadow-medium focus-visible:border-primary-500/50 focus-visible:ring-2 focus-visible:ring-primary-400/30"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="mono-label !text-primary-500">
                  {project.kind}
                </span>
                <span className="w-px h-4 bg-neutral-300 dark:bg-white/15" />
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-400 dark:text-neutral-500">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <ArrowUpRight
                  className="ml-auto text-neutral-400 group-hover:text-primary-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-200"
                  size={18}
                />
              </div>

              <h3 className="font-display text-2xl font-semibold tracking-tight group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                {project.title}
              </h3>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">
                {project.role}
              </p>
              <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                {project.tagline}
              </p>

              <ul className="mt-6 space-y-2">
                {project.results.slice(0, 3).map((result) => (
                  <li
                    key={result}
                    className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-neutral-300"
                  >
                    <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-md bg-primary-500/15 text-primary-600 dark:text-primary-400 flex-shrink-0">
                      <Check size={12} />
                    </span>
                    {result}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-wrap gap-2">
                {project.technologies.slice(0, 6).map((tech) => (
                  <span
                    key={tech}
                    className="inline-flex items-center rounded-lg border border-neutral-200 dark:border-white/8 px-2.5 py-1 font-mono text-[11px] text-neutral-500 dark:text-neutral-400"
                  >
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 6 && (
                  <span className="inline-flex items-center rounded-lg border border-neutral-200 dark:border-white/8 px-2.5 py-1 font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
                    +{project.technologies.length - 6}
                  </span>
                )}
              </div>

              <div className="mt-6 flex items-center justify-between gap-4 border-t border-neutral-200 dark:border-white/6 pt-5">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400">
                  Read the case study
                  <ArrowUpRight size={15} />
                </span>
                <a
                  href={project.repo}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${project.title} source code on GitHub`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors"
                >
                  <ExternalLink size={14} />
                  Source
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Projects